import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import Moment from 'moment';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import BackgroundImage from '../components/BackgroundImage';
import TopSection, { screenMaxWidth, TopSectionProps } from '../components/TopSection';
import Colors from '../constants/Colors';
import ScreenEmptyState from '../components/ScreenEmptyState';
import { MetricPropTypes, MetricProps } from '../graphs/AirQualityGraph';
import { DateRange, DateSelectionRange, updateDateRange } from '../redux/dateRangeSlice';
import { updateDateComparisonRange } from '../redux/dateComparisonRangeSlice';
import { HappyScoreCategory, HappyScoreCategoryTitles, Metric } from '../constants/Types';
import isMobile from '../components/IsMobile';
import MobileHeader from './InternalEnvironment/components/MobileHeader';
import GetMetric, { AggregationFunction } from '../requests/Sensors/GetMetric';
import OverviewMobile from './InternalEnvironment/components/OverviewMobile';
import MetricSelectionRow from './InternalEnvironment/components/MetricSelectionRow';
import GetExternalEnvironmentData from '../requests/Sensors/GetExternalEnvironmentData';
import AirQualitySection from './InternalEnvironment/components/AirQuality';
import GetMetricHappyScoreData from '../requests/Sensors/GetMetricHappyScoreData';
import FixedDateComponent from '../components/FixedDateComponent';

export default function ExternalEnvironmentScreen({ route }) {
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));
  const compareRange: DateRange = useSelector((state: { dateComparisonRange: DateRange }) => (state
    .dateComparisonRange));
  const dispatch = useDispatch();
  const scrollViewRef = useRef(null);
  const setRange = (dates: DateRange) => dispatch(updateDateRange(dates));
  const setCompareRange = (dates: DateRange) => dispatch(updateDateComparisonRange(dates));
  const notEmpty: boolean | undefined | null = useSelector((state:
    { emptyState: { notEmpty: boolean } }) => state.emptyState.notEmpty);

  const onConfirm = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setRange(
      { start, end, selectedRange },
    ),
    [setRange],
  );

  useEffect(() => {
    setTimeout(() => {
      const graph = route.params?.metricIndex;
      if (graph === 0 || graph) scrollViewRef.current.scrollToEnd({ animated: true });
    }, 500);
  }, [route]);

  const onCompareConfirm = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setCompareRange(
      { start, end, selectedRange },
    ),
    [setCompareRange],
  );

  const topSectionProps: TopSectionProps = {
    screen: HappyScoreCategory.EXTERNAL_ENVIRONMENT,
  };

  const metrics: MetricProps[] = [
    {
      title: 'Rain',
      scoreValue: '1_01_happyScore',
      type: MetricPropTypes.PERCENTAGE,
      unit: 'Happy score %',
      description: 'Rain data indicates the amount and intensity of precipitation in the area. Light to moderate rain can influence mood and comfort, while heavy rainfall may disrupt outdoor activities and travel. Lower levels of rain are generally associated with a higher Happy Score. Only Calculated during Business Hours.',
    },
    {
      title: 'Cloud Cover',
      unit: 'Happy score %',
      scoreValue: '1_02_happyScore',
      type: MetricPropTypes.PERCENTAGE,
      description: 'Weather is delivered as Weather Codes from our Weather API providers. We are particularly interested in Sunny/Fine weather as distinct from Thundery or Overcast Weather. Sunny/Fine weather will positively impact the Happy Score.',
    },
    {
      title: 'Temperature',
      unit: 'Happy score %',
      scoreValue: '1_03_happyScore',
      type: MetricPropTypes.PERCENTAGE,
      description: 'Temperature refers to the ambient outdoor heat level. Comfortable temperature ranges (typically mild to warm) contribute positively to well-being and satisfaction. Extreme heat or cold can reduce comfort and negatively impact the Happy Score. Only Calculated during Business Hours.',
    },
    {
      title: 'Wind Speed',
      unit: 'Happy score %',
      scoreValue: '1_04_happyScore',
      type: MetricPropTypes.PERCENTAGE,
      description: 'Wind Speed measures the movement of air in the environment. Gentle breezes may enhance outdoor comfort, while high wind speeds can cause discomfort or safety concerns. Optimal wind conditions support a higher Happy Score. Only Calculated during Business Hours.',
    },
    {
      title: 'Travel Time',
      unit: 'Happy score %',
      scoreValue: '1_05_happyScore',
      type: MetricPropTypes.PERCENTAGE,
      description: 'Travel Time is the travel time calculated by Google Maps from the selected building to a predefined point(s). A Shorter time is preferable and will positively affect the Happy Score.',
    },
    {
      table: Metric.OCCUPANCY,
      title: 'Occupancy',
      unit: 'People',
      scoreValue: '2_05_happyScore',
      excludeLocation: true,
      aggregationFunction: AggregationFunction.MAX,
      description: 'Occupancy measures the number of people in a space at any time. Tracking occupancy helps adjust air quality, temperature, and ventilation to match occupant needs. Higher occupancy can increase CO₂ levels and discomfort, so managing it improves occupant comfort and energy efficiency.',
    },
  ];

  const [liveValues] = useState<number[]>(metrics.map(() => null));
  const [selectedMetric, setSelectedMetric] = useState<MetricProps>(null);
  const [deepLinkMetric, setDeepLinkMetric] = useState(null);
  const [graphData, setGraphData] = useState<object[][]>(metrics.map(() => []));
  const [graphComparisonData, setGraphComparisonData] = useState<object[][]>(metrics.map(() => []));
  const metricTitles: string[] = metrics.map((metric: MetricProps) => metric.title);

  useEffect(() => {
    if (!isMobile) return;
    metrics.forEach((metric: MetricProps, index: number) => {
      const liveDateRange: DateRange = {
        start: Moment().subtract(60, 'minutes').toDate().toISOString(),
        end: new Date().toISOString(),
      };
      if (metric.index) {
        GetExternalEnvironmentData({
          metric: metric.index,
          field: metric.field,
          range: liveDateRange,
        })?.then((metricData) => {
          const value: number | null = metricData ? metricData[metricData.length - 1]?.value : null;
          liveValues[index] = (typeof value === 'number' && value > -1)
            ? Math.round(10 * value) / 10 : null;
        });
      } else {
        (metric.table ? GetMetric({
          metric: metric.table,
          range: liveDateRange,
        }) : GetMetricHappyScoreData({
          metric: metric.scoreValue,
          range: liveDateRange,
        }))?.then((data) => {
          if (!(data?.length > 0)) return;
          if (data.source) {
            liveValues[index] = Math.round(data.source / (metric.factor ?? 1));
          } else {
            const dataPoints = data?.filter((item) => typeof item.value === 'number')
              .map((item) => item.value);
            const value: number = dataPoints ? dataPoints[dataPoints.length - 1] : null;
            liveValues[index] = value > -1 ? Math.round(value) : null;
          }
        });
      }
    });
  }, []);

  useEffect(() => {
    const newData = metrics.map(() => []);
    metrics?.forEach((metric: MetricProps, metricIndex: number) => {
      (metric.table ? GetMetric({
        metric: metric.table,
        range,
        aggregationFunction: metric.aggregationFunction,
      })
        : GetMetricHappyScoreData({ metric: metric.scoreValue, range }))
        ?.then((metricData) => {
          (metricData ?? []).forEach((e, index: number) => newData[metricIndex]?.push({
            value: (e.value ?? 0) / (metric.factor ?? 1),
            date: e.timestamp ?? e.date,
            index,
          }));
          setGraphData([...newData]);
        });
    });
  }, [range]);

  useEffect(() => {
    const newData = metrics.map(() => []);
    metrics?.forEach((metric: MetricProps, metricIndex: number) => {
      (metric.table ? GetMetric({ metric: metric.table, range: compareRange })
        : GetMetricHappyScoreData({ metric: metric.scoreValue, range: compareRange }))
        ?.then((metricData) => {
          (metricData ?? []).forEach((e, index: number) => newData[metricIndex]?.push({
            value: (e.value ?? 0) / (metric.factor ?? 1),
            date: e.timestamp ?? e.date,
            index,
          }));
          setGraphComparisonData([...newData]);
        });
    });
  }, [compareRange]);

  useEffect(() => {
    const metricIndex = route.params?.metricIndex;
    if (metricIndex === 0 || metricIndex) {
      setDeepLinkMetric(metricIndex);
    }
  }, [route]);

  if (isMobile) {
    return <MobileHeader
      happyScoreMetric='group1ExternalEnvironment'
      screen={HappyScoreCategory.EXTERNAL_ENVIRONMENT}
      overview={<OverviewMobile {...{ metrics, liveValues, setSelectedMetric }} />}
      showContent={() => setSelectedMetric(null)}
      graphTitle={selectedMetric?.title}
      graphData={graphData[metricTitles.indexOf(selectedMetric?.title)]}
      graphComparisonData={graphComparisonData[metricTitles.indexOf(selectedMetric?.title)]}
      historyFooter={<MetricSelectionRow
        {...{ setSelectedMetric }}
        titles={metrics.map((metric: MetricProps) => metric.title)}
        selectedTitle={selectedMetric?.title}
        select={(index: number) => setSelectedMetric(metrics[index])}
      />}
      buildingSelectionOnly
    />;
  }

  return !notEmpty ? <ScreenEmptyState large /> : <View style={{ flex: 1 }}>
    <ScrollView style={styles.container}
      ref={scrollViewRef}>
      <BackgroundImage />
      <TopSection {...topSectionProps} />
      <View style={[styles.content, { maxWidth: screenMaxWidth }]}>
        <AirQualitySection
          title={HappyScoreCategoryTitles[HappyScoreCategory.EXTERNAL_ENVIRONMENT]}
          overallScoreIndex='group1ExternalEnvironment'
          deepLinkMetric={deepLinkMetric}
          {...{ range, compareRange, metrics }}
        />
      </View>
    </ScrollView>
    <FixedDateComponent
      onConfirm={onConfirm}
      onCompareConfirm={onCompareConfirm}
      liveEnabled
    />
  </View>;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.light.hereworksBackground,
  },
  content: {
    paddingHorizontal: 40,
  },
});
